import { useLayoutEffect } from 'react';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function useLandingAnimations() {
    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            // Hero Animations
            const tl = gsap.timeline();

            const revealText = gsap.utils.toArray<HTMLElement>('.reveal-text');
            if (revealText.length > 0) {
                tl.from(revealText, {
                    y: 42,
                    opacity: 0,
                    duration: 0.9,
                    stagger: 0.11,
                    ease: 'power3.out'
                });
            }

            const detailHighlights = gsap.utils.toArray<HTMLElement>('.landing-detail-highlight-card');
            if (detailHighlights.length > 0) {
                tl.from(detailHighlights, {
                    y: 24,
                    opacity: 0,
                    scale: 0.98,
                    duration: 0.72,
                    stagger: 0.08,
                    ease: 'power3.out'
                }, revealText.length > 0 ? '-=0.42' : 0);
            }

            const revealImg = document.querySelector('.reveal-img');
            if (revealImg) {
                // If text exists, overlap by 0.5s. If not, start immediately.
                const position = revealText.length > 0 ? '-=0.5' : 0;
                tl.from(revealImg, {
                    y: 70,
                    opacity: 0,
                    scale: 0.97,
                    duration: 1.05,
                    ease: 'power4.out'
                }, position);
            }

            const heroFloatingCards = gsap.utils.toArray<HTMLElement>('.landing-detail-hero-floating-card');
            if (heroFloatingCards.length > 0) {
                tl.from(heroFloatingCards, {
                    y: 28,
                    opacity: 0,
                    scale: 0.96,
                    duration: 0.75,
                    stagger: 0.1,
                    ease: 'power3.out'
                }, '-=0.55');
            }

            const homeSections = gsap.utils.toArray<HTMLElement>([
                '.clinic-comparison-section',
                '.workflow-feature-section',
                '.ai-scribe-section',
                '.roi-section',
                '.integrations-section',
                '.practice-types-section',
                '.capabilities-section',
                '.contact-section',
            ].join(', '));

            homeSections.forEach((section) => {
                const primaryElements = gsap.utils.toArray<HTMLElement>(
                    section.querySelectorAll([
                        '.clinic-comparison-container > h2',
                        '.clinic-comparison-panel',
                        '.clinic-comparison-bridge',
                        '.workflow-feature-copy',
                        '.workflow-feature-visual',
                        '.ai-scribe-header',
                        '.ai-scribe-card',
                        '.roi-container > h2',
                        '.roi-card',
                        '.integrations-header',
                        '.integrations-map',
                        '.practice-types-container > h2',
                        '.practice-type-card',
                        '.practice-types-cta',
                        '.capabilities-container > h2',
                        '.capability-column',
                        '.contact-section .container > h2',
                        '.contact-section .container > p',
                        '.contact-form',
                    ].join(', '))
                );

                if (!primaryElements.length) {
                    return;
                }

                gsap.from(primaryElements, {
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 78%',
                        once: true,
                    },
                    y: 54,
                    opacity: 0,
                    scale: 0.985,
                    duration: 0.85,
                    ease: 'power3.out',
                    stagger: {
                        each: 0.07,
                        from: 'start',
                    },
                });
            });

            const animatedVisuals = gsap.utils.toArray<HTMLElement>([
                '.workflow-feature-phone',
                '.operations-report-card',
                '.operations-success-card',
                '.growth-template-card',
                '.growth-campaign-card',
                '.ai-scribe-orbit',
                '.ai-scribe-listening',
                '.ai-scribe-wave',
            ].join(', '));

            animatedVisuals.forEach((visual) => {
                gsap.from(visual, {
                    scrollTrigger: {
                        trigger: visual,
                        start: 'top 88%',
                        once: true,
                    },
                    y: 28,
                    opacity: 0,
                    scale: 0.94,
                    duration: 0.7,
                    ease: 'back.out(1.4)',
                });
            });

            // Deep Dive Reveal
            const sections = document.querySelectorAll('.deep-dive');
            sections.forEach(section => {
                const content = section.querySelector('.dd-content');
                const image = section.querySelector('.dd-image');

                if (content) {
                    gsap.from(content, {
                        scrollTrigger: {
                            trigger: section,
                            start: 'top 80%',
                        },
                        y: 50,
                        opacity: 0,
                        duration: 1,
                        ease: 'power3.out'
                    });
                }

                if (image) {
                    gsap.from(image, {
                        scrollTrigger: {
                            trigger: section,
                            start: 'top 80%',
                        },
                        y: 80,
                        opacity: 0,
                        duration: 1.2,
                        ease: 'power3.out',
                        delay: 0.2
                    });
                }
            });

            // Features Grid Stagger
            const features = document.querySelectorAll('.feature-item');
            features.forEach((item, i) => {
                gsap.from(item, {
                    scrollTrigger: {
                        trigger: item,
                        start: 'top 90%',
                        once: true,
                    },
                    y: 30,
                    opacity: 0,
                    duration: 0.6,
                    delay: (i % 3) * 0.1
                });
            });

            const animatedCards = gsap.utils.toArray<HTMLElement>('.landing-animate-card');
            animatedCards.forEach((card, index) => {
                gsap.from(card, {
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 86%',
                        once: true,
                    },
                    y: 46,
                    opacity: 0,
                    scale: 0.985,
                    duration: 0.82,
                    ease: 'power3.out',
                    delay: Math.min((index % 3) * 0.05, 0.12),
                });
            });

            const galleryCards = gsap.utils.toArray<HTMLElement>('.landing-detail-gallery-card');
            galleryCards.forEach((card, index) => {
                const imageShell = card.querySelector<HTMLElement>('.landing-detail-gallery-image-shell');
                const frame = card.querySelector<HTMLElement>('.landing-detail-gallery-screen-frame');
                const copy = card.querySelector<HTMLElement>('.landing-detail-gallery-copy');
                const image = card.querySelector<HTMLImageElement>('img');
                const fromX = index % 2 === 0 ? -48 : 48;

                gsap.from(card, {
                    scrollTrigger: {
                        trigger: card,
                        start: 'top 80%',
                        once: true,
                    },
                    y: 72,
                    opacity: 0,
                    duration: 0.95,
                    ease: 'power3.out',
                });

                if (imageShell) {
                    gsap.from(imageShell, {
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 82%',
                            once: true,
                        },
                        x: fromX,
                        opacity: 0,
                        duration: 1,
                        ease: 'power3.out',
                    });
                }

                if (copy) {
                    gsap.from(copy, {
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 82%',
                            once: true,
                        },
                        x: -fromX * 0.72,
                        opacity: 0,
                        duration: 0.9,
                        ease: 'power3.out',
                    });
                }

                if (frame) {
                    gsap.from(frame, {
                        scrollTrigger: {
                            trigger: card,
                            start: 'top 84%',
                            once: true,
                        },
                        scale: 0.92,
                        rotateX: 6,
                        transformOrigin: 'center center',
                        opacity: 0,
                        duration: 1,
                        ease: 'power3.out',
                    });
                }

                if (image) {
                    gsap.fromTo(image,
                        {
                            y: 26,
                            scale: 1.05,
                        },
                        {
                            y: index % 2 === 0 ? -18 : 18,
                            scale: 1,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: card,
                                start: 'top bottom',
                                end: 'bottom top',
                                scrub: true,
                            },
                        },
                    );
                }
            });
        });

        return () => ctx.revert();
    }, []);
}
